import { useState, useCallback } from "react";
import type { SuggestedPackingItemDto, PackingItemResponseDto } from "@tabipla/shared/types";
import {
  filterByExistingNames,
  getEssentialIndices,
  toggleSetItem,
  selectAllIndices,
  getSelectedItems,
} from "@/utils/packing-list";
import { useAISuggestPackingItems, useBulkCreatePackingItems } from "./usePacking";
import { useErrorHandler } from "./useErrorHandler";

export interface UsePackingAiSuggestionsReturn {
  aiSuggestions: SuggestedPackingItemDto[];
  selectedSuggestions: Set<number>;
  showAiPanel: boolean;
  isAiLoading: boolean;
  isBulkCreating: boolean;
  onAiSuggest: () => Promise<void>;
  onToggleSuggestion: (index: number) => void;
  onSelectAllSuggestions: () => void;
  onDeselectAllSuggestions: () => void;
  onAddSelectedSuggestions: () => Promise<void>;
  onCloseAiPanel: () => void;
}

/**
 * Hook for managing AI-powered packing suggestions.
 */
export function usePackingAiSuggestions(
  tripId: string,
  items: PackingItemResponseDto[]
): UsePackingAiSuggestionsReturn {
  const { handleError, showSuccess, t } = useErrorHandler();

  const aiSuggestMutation = useAISuggestPackingItems(tripId);
  const bulkCreateMutation = useBulkCreatePackingItems(tripId);

  const [aiSuggestions, setAiSuggestions] = useState<SuggestedPackingItemDto[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());
  const [showAiPanel, setShowAiPanel] = useState(false);

  const onAiSuggest = useCallback(async () => {
    try {
      const suggestions = await aiSuggestMutation.mutateAsync();
      if (suggestions) {
        const filteredSuggestions = filterByExistingNames(suggestions, items);
        setAiSuggestions(filteredSuggestions);
        setSelectedSuggestions(getEssentialIndices(filteredSuggestions));
        setShowAiPanel(true);
      }
    } catch (error) {
      handleError(error, { message: "AI提案の取得に失敗しました" });
    }
  }, [aiSuggestMutation, items, handleError]);

  const onToggleSuggestion = useCallback((index: number) => {
    setSelectedSuggestions((prev) => toggleSetItem(prev, index));
  }, []);

  const onSelectAllSuggestions = useCallback(() => {
    setSelectedSuggestions(selectAllIndices(aiSuggestions));
  }, [aiSuggestions]);

  const onDeselectAllSuggestions = useCallback(() => {
    setSelectedSuggestions(new Set());
  }, []);

  const onAddSelectedSuggestions = useCallback(async () => {
    const selectedItems = getSelectedItems(aiSuggestions, selectedSuggestions).map((s) => ({
      name: s.name,
      // AI が isEssential と判断したアイテムは priority: 3 (最高優先度) を設定
      priority: s.isEssential ? 3 : null,
    }));

    if (selectedItems.length === 0) return;

    try {
      await bulkCreateMutation.mutateAsync({ items: selectedItems });
      setShowAiPanel(false);
      setAiSuggestions([]);
      setSelectedSuggestions(new Set());
      showSuccess(
        "packing.suggestionsAdded",
        t("packing.suggestionsAdded", {
          count: selectedItems.length,
          defaultValue: `${selectedItems.length}件の提案を追加しました`,
        })
      );
    } catch (error) {
      handleError(error, { message: "提案の追加に失敗しました" });
    }
  }, [aiSuggestions, selectedSuggestions, bulkCreateMutation, showSuccess, handleError, t]);

  const onCloseAiPanel = useCallback(() => {
    setShowAiPanel(false);
  }, []);

  return {
    aiSuggestions,
    selectedSuggestions,
    showAiPanel,
    isAiLoading: aiSuggestMutation.isPending,
    isBulkCreating: bulkCreateMutation.isPending,
    onAiSuggest,
    onToggleSuggestion,
    onSelectAllSuggestions,
    onDeselectAllSuggestions,
    onAddSelectedSuggestions,
    onCloseAiPanel,
  };
}
